import { Drawer, Form, Spin, message } from 'antd';
import { cloneDeep } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useDeleteMappedEvent,
  useGetDetailsMappedEvent,
  usePostMappedEvents,
  useUpdateMappedEvent,
} from '@api/emp/queryHooks';
import { EPanelOpenMethod } from '@components/block-event-single/store';
import { EventTitle } from '@components/detail-panel/components/EventInfoCard/styled';
import { MappedRules } from '@components/detail-panel/components/EventMappingRules';
import EventStatusCard from '@components/detail-panel/components/EventStatusCard';
import { IMappedDetailPanel } from '@components/detail-panel/components/MappedDetailPanel/typed';
import { MetricsRelatedCard } from '@components/detail-panel/components/MetricsRelatedCard';
import ToolBar from '@components/detail-panel/components/ToolBar';
import { useMappedDetailPanelStore } from '@components/detail-panel/store';
import { DrawerMainLeft, DrawerMainRight } from '@components/detail-panel/styled';
import EventInfoForm from '@components/mapped-event-management/components/EventInfoForm';
import { useMappingPanelStore } from '@components/mapping-panel/store';
import { transformRulesToState } from '@components/mapping-panel/utils/handleRulesUtils';
import { validateMappedEventData } from '@components/mapping-panel/utils/validateUtils';
import { useTopPanelStore } from '@hooks';
import { MappedEventTypes } from '@src/common/types/empTypes';
import { MappedEvent } from '@src/emp-3.0/types/mapped-event';
import { EDraftStatus } from '@src/emp-3.0/types';
import {
  SEMANTIC_MAPPER,
  trackMapNewEvent,
  trackUpdateMappedEvent,
  trackViewinSemanticClick,
} from '@src/sensor';
import {
  useCheckDuplicateMappedEvent,
  useUpdateActivateMappedEvent,
} from '@src/emp-3.0/api/queryHooks';
import { addIsManualFieldToMappedEvent } from '@components/mapping-panel/store/sensorStore';

const MappedDetailPanel: React.FC<IMappedDetailPanel> = ({
  open = false,
  onClose,
  mappedEvent,
  fixedWidth = 300,
  operationMode,
  setOperationMode,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [
    mappedEventData,
    mappedEventDataDraft,
    setMappedEventData,
    setMappedEventDataDraft,
    resetMappedDetailPanelStore,
  ] = useMappedDetailPanelStore((state) => [
    state.mappedEventData,
    state.mappedEventDataDraft,
    state.setMappedEventData,
    state.setMappedEventDataDraft,
    state.resetMappedDetailPanelStore,
  ]);
  const [setMappedEvent, setEventCategory, setMappedName, setMappingRules] = useMappingPanelStore(
    (state) => [
      state.setMappedEvent,
      state.setEventCategory,
      state.setMappedName,
      state.setMappingRules,
    ],
  );
  const { openMappingPanel } = useTopPanelStore();

  const { data: mappedEventRes, isFetching: isEventDetailFetching } = useGetDetailsMappedEvent({
    event_id: mappedEvent?.event_id,
    version_id: mappedEvent?.version_id,
  });

  const { mutate: create, isLoading: createMappedEventLoading } = usePostMappedEvents();
  const { mutate: update, isLoading: isUpdateMappedEventLoading } = useUpdateMappedEvent();
  const { mutate: updateActivateMappedEvent, isLoading: isUpdateActivateMappedEventLoading } =
    useUpdateActivateMappedEvent();
  const { mutate: deleteMappedEvent, isLoading: isDeleteMappedEventLoading } =
    useDeleteMappedEvent();

  const [duplicateMappedEventName, setDuplicateMappedEventName] = useState<string>('');
  const { mutate: checkDuplicateMappedEvent } = useCheckDuplicateMappedEvent();

  const loading =
    isEventDetailFetching ||
    isUpdateMappedEventLoading ||
    isDeleteMappedEventLoading ||
    createMappedEventLoading ||
    isUpdateActivateMappedEventLoading;

  const isReadOnly = useMemo(
    () => mappedEvent?.is_multi_version && mappedEvent.is_draft !== EDraftStatus.isDraft,
    [mappedEvent],
  );
  const isViewMode = useMemo(() => operationMode === EPanelOpenMethod.VIEW, [operationMode]);
  const mappedEventResData = useMemo(
    () => (mappedEventRes as { data: MappedEvent })?.data,
    [mappedEventRes],
  );

  useEffect(() => {
    const suffix = operationMode === EPanelOpenMethod.CLONE ? '_Copy' : '';
    if (mappedEventResData) {
      const frontendGeneratedFields = {
        event_id: mappedEvent?.event_id,
        row_number: mappedEvent?.row_number,
        id: mappedEvent?.id,
        is_multi_version: mappedEvent?.is_multi_version,
      };
      const eventName = mappedEventResData.event_name + suffix;
      setMappedEventDataDraft(
        cloneDeep({ ...frontendGeneratedFields, ...mappedEventResData, event_name: eventName }),
      );
      setMappedEventData({
        ...frontendGeneratedFields,
        ...mappedEventResData,
        event_name: eventName,
      });
    }
  }, [mappedEventResData, form, operationMode]);

  useEffect(() => {
    const fieldsValue = {
      event_name: mappedEventDataDraft?.event_name,
      event_category: mappedEventDataDraft?.event_category,
      description: mappedEventDataDraft?.description,
    };
    form.setFieldsValue(fieldsValue);
    setDuplicateMappedEventName('');
  }, [mappedEventDataDraft]);

  const handleRuleChange = (value) => {
    setMappedEventDataDraft(value);
  };

  const handleEditClick = () => {
    setOperationMode(EPanelOpenMethod.EDIT);
  };

  const handleCloneClick = () => {
    setOperationMode(EPanelOpenMethod.CLONE);
  };

  const handleCancelClick = () => {
    setOperationMode(EPanelOpenMethod.VIEW);
    mappedEventData && setMappedEventDataDraft(mappedEventData);
  };

  const handleCloseClick = () => {
    onClose?.();
    resetMappedDetailPanelStore();
  };

  const updateMappedEvent = (id, mappedEvent) => {
    const updateHandler = (updateFn, payload: any) => {
      updateFn(payload, {
        onSuccess: () => {
          setMappedEventData(mappedEventDataDraft!);
          setOperationMode(EPanelOpenMethod.VIEW);
          handleCloseClick();
          const mappedEventWithIsManual = addIsManualFieldToMappedEvent(mappedEvent);
          trackUpdateMappedEvent({
            event_name: mappedEvent.event_name,
            event_category: mappedEvent.event_category,
            isManual: mappedEventWithIsManual.isManual,
            mappedEventWithIsManual: mappedEventWithIsManual,
          });
          message.success('Update success!');
        },
        onError: (error: any) => {
          const errorMsg = error?.response?.data?.msg || 'An error occurred.';
          message.error(errorMsg);
        },
      });
    };

    updateHandler(update, {
      id,
      mappedEvent,
    });
  };

  const createMappedEvent = (res: any) => {
    create(res.mappedEvent, {
      onSuccess: () => {
        message.success('create success!');
        setOperationMode(EPanelOpenMethod.VIEW);
        handleCloseClick();
        const mappedEventWithIsManual = addIsManualFieldToMappedEvent(res.mappedEvent);
        trackMapNewEvent({
          event_name: res.mappedEvent.event_name,
          event_category: res.mappedEvent.event_category,
          type: SEMANTIC_MAPPER,
          isManual: mappedEventWithIsManual.isManual,
          mappedEventWithIsManual: mappedEventWithIsManual,
        });
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.msg);
      },
    });
  };

  const handleDeleteMappedEvent = () => {
    const eventId = mappedEvent?.event_id;
    if (eventId) {
      deleteMappedEvent(eventId, {
        onSuccess: () => {
          handleCloseClick();
          message.success('Delete success!');
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
        },
      });
    }
  };

  const handleUpdateCreate = (queryBody) => {
    if (operationMode === EPanelOpenMethod.EDIT) {
      updateMappedEvent(mappedEvent?.event_id, queryBody);
      return;
    }
    createMappedEvent({ mappedEvent: queryBody });
  };

  const handleSaveClick = (ignoreCheckDuplicate = false) => {
    if (mappedEventDataDraft) {
      const relatedMetricNames = (mappedEventDataDraft?.related_metrics || []).map(
        (item) => item.name,
      );
      const res = validateMappedEventData(
        {
          ...mappedEventDataDraft,
          related_metrics: relatedMetricNames,
        } as MappedEventTypes,
        false,
      );
      if (!res.status) {
        message.warning(`${res.key ? res.key.join(' OR ') : ''} ${res.result}`).then();
        return;
      }
      const queryBody = {
        event_name: mappedEventDataDraft.event_name,
        event_category: mappedEventDataDraft.event_category,
        description: mappedEventDataDraft.description,
        rules: res.mappedEvent.rules,
      };
      if (ignoreCheckDuplicate) {
        setDuplicateMappedEventName('');
        handleUpdateCreate(queryBody);
        return;
      }
      checkDuplicateMappedEvent(
        { ...queryBody },
        {
          onSuccess: (res) => {
            let duplicateMappedEvent = res.data[0]?.event_name || '';
            if (operationMode === EPanelOpenMethod.EDIT) {
              duplicateMappedEvent =
                res.data.filter((dup) => dup.id !== mappedEvent?.event_id)[0]?.event_name || '';
            }
            setDuplicateMappedEventName(duplicateMappedEvent);
            if (duplicateMappedEvent) {
              return;
            }
            handleUpdateCreate(queryBody);
          },
          onError: (res) => {
            message.error('Failed To Check Duplicate Mapped Event');
            handleUpdateCreate(queryBody);
          },
        },
      );
    }
  };

  const handleClickViewButton = () => {
    if (mappedEventDataDraft) {
      trackViewinSemanticClick({ type: 'Mapped Event', ...mappedEventDataDraft });
      openMappingPanel();
      setMappedEvent({
        ...mappedEventDataDraft,
        event_id: mappedEvent?.event_id,
        related_metrics: (mappedEventDataDraft?.related_metrics ?? [])?.map((item) => item.name),
      } as MappedEventTypes);
      setEventCategory(mappedEventDataDraft.event_category);
      setMappedName(mappedEventDataDraft.event_name);
      setMappingRules(transformRulesToState(mappedEventDataDraft.rules as any));
      navigate(`/semantic-mapper`);
    }
  };

  return (
    <Drawer
      open={open}
      closable={false}
      mask={false}
      width={`calc(100% - ${fixedWidth}px)`}
      bodyStyle={{ padding: 0 }}
      getContainer={false}
      destroyOnClose
      forceRender
    >
      <Spin spinning={loading}>
        <ToolBar
          duplicateMappedEventName={duplicateMappedEventName}
          setDuplicateMappedEventName={setDuplicateMappedEventName}
          mode={operationMode}
          data={mappedEventDataDraft}
          onClose={handleCloseClick}
          onEdit={handleEditClick}
          onCancel={handleCancelClick}
          onSave={() => handleSaveClick(false)}
          onDelete={handleDeleteMappedEvent}
          onClone={handleCloneClick}
          onSaveAs={() => handleSaveClick(false)}
        />
        <div style={{ display: 'flex' }}>
          <DrawerMainLeft>
            <EventTitle>Details of “{mappedEventDataDraft?.event_name}”</EventTitle>
            <EventInfoForm
              mode={operationMode}
              mappedEvent={mappedEvent}
              form={form}
              disabled={isViewMode}
              onValuesChange={(changedValues, values) => {
                setMappedEventDataDraft({ ...mappedEventDataDraft, ...values });
              }}
            />
            <MappedRules
              readOnly={isReadOnly}
              mode={operationMode}
              mappedEvent={mappedEventDataDraft}
              onChange={handleRuleChange}
              onClick={handleClickViewButton}
            />
          </DrawerMainLeft>
          {operationMode !== EPanelOpenMethod.CLONE && (
            <DrawerMainRight>
              <EventStatusCard mappedEvent={mappedEventDataDraft} />
              <MetricsRelatedCard
                mode={operationMode}
                mappedEvent={mappedEventDataDraft}
                onCloseDrawer={handleCloseClick}
              />
            </DrawerMainRight>
          )}
        </div>
      </Spin>
    </Drawer>
  );
};

export default MappedDetailPanel;
